import React from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useDisconnectShopify } from 'hooks/api'

import { useTheme, Stack, Typography } from '@mui/material'

import { Modal, Button, Alert } from '@repo/ui'

interface DisconnectIntegrationModalProps {
  integration: any
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const DisconnectIntegrationModal = (props: DisconnectIntegrationModalProps) => {
  const { integration, open, onClose = () => {}, refetch } = props
  const theme = useTheme()

  const { open: notification } = useSnackbar()

  const { disconnectShopify, isDisconnecting } = useDisconnectShopify()
  const isShopify = integration?.name?.toLowerCase() === 'shopify'

  const handleDisconnect = async () => {
    if (!integration) return

    try {
      if (isShopify) {
        await disconnectShopify()
      } else {
        // For other integrations, you can implement their disconnect logic here
        // For now, we'll just show a success message
        notification({
          type: 'success',
          title: `Your ${integration?.name} integration has been successfully disconnected`,
        })
      }
      
      handleClose()
      refetch()
    } catch (error) {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    }
  }

  const handleClose = () => {
    onClose()
  }

  const isLoading = isShopify ? isDisconnecting : false

  return (
    <Modal
      title={`Disconnect ${integration?.name}`}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={() => handleClose()}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={() => handleDisconnect()}
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography variant="body2" color="text.secondary">
          {`Are you sure you want to disconnect ${integration?.name}?`}
        </Typography>
        {isShopify && (
          <Alert
            severity="warning"
            description="This will remove your Shopify store connection and you'll need to reconnect if you want to use Shopify features again."
          />
        )}
      </Stack>
    </Modal>
  )
}
